import React, {useRef} from 'react';
import {useIntl} from 'react-intl';
import {ACTION_BUTTON_SIZE, ActionButton, EMPHASIS, FlexRow, Tooltip} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import type {AgentDeclaration, AgentPage, AgentRow} from './agentDeclaration';
import {ensureAgentsRegistered, getVisibleAgentsForRow} from './agentRegistry';
import {AGENT_ROW_STATUS, useAgentRowState} from './agentRunState';

ensureAgentsRegistered();

type AgentRowActionProps = {
	declaration: AgentDeclaration;
	row: AgentRow;
};

// One agent's control on one row. A component of its own because each agent holds its own run state.
// Every state renders the same main part, so React keeps its button and the focus Stop hands to it.
const AgentRowAction = ({declaration, row}: AgentRowActionProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {status, result, inPageRun, startRun, stopRun} = useAgentRowState(declaration, row);
	const actionRef = useRef<HTMLButtonElement>(null);
	const isWorking = status === AGENT_ROW_STATUS.WORKING;
	const isReady = status === AGENT_ROW_STATUS.READY;

	const actionLabel = isWorking
		? formatMessage({id: AGENTIC_WORKERS_MESSAGE.ROW_ACTION_WORKING})
		: isReady
		? formatMessage({id: AGENTIC_WORKERS_MESSAGE.ROW_ACTION_READY})
		: formatMessage(declaration.action);
	const rowActionTip = isWorking
		? declaration.rowActionTips.working
		: isReady
		? declaration.rowActionTips.ready
		: declaration.rowActionTips.idle;
	const stopLabel = formatMessage({
		id: inPageRun ? AGENTIC_WORKERS_MESSAGE.ROW_ACTION_STOP_PAGE_RUN : AGENTIC_WORKERS_MESSAGE.ROW_ACTION_STOP,
	});
	const readyMenuLabel = formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.ROW_ACTION_READY_MENU},
		{result: formatMessage(declaration.resultNoun.singular)}
	);

	const pressAction = (): void => {
		if (isReady && result) {
			declaration.review(result);
			return;
		}
		startRun();
	};

	// The stop part disappears once the run stops, so focus is handed to the main part instead of falling back to the page.
	const stopFromRow = (): void => {
		stopRun();
		actionRef.current?.focus();
	};

	const trailingAction = isWorking
		? {
				icon: 'stop' as const,
				'aria-label': stopLabel,
				tooltip: stopLabel,
				onClick: stopFromRow,
				'data-cy': 'agent-row-action-stop',
		  }
		: isReady
		? {
				icon: 'chevronDown' as const,
				'aria-label': readyMenuLabel,
				tooltip: readyMenuLabel,
				'data-cy': 'agent-row-action-ready-menu',
		  }
		: undefined;

	return (
		<FlexRow alignItems={'center'} flex={'0 0 auto'} data-cy={`agent-row-action-${declaration.id}`}>
			<Tooltip content={formatMessage(rowActionTip)}>
				<ActionButton
					ref={actionRef}
					size={ACTION_BUTTON_SIZE.COMPACT}
					emphasis={isReady ? EMPHASIS.HIGH : EMPHASIS.MEDIUM}
					icon={'sparkle'}
					waiting={isWorking}
					trailingAction={trailingAction}
					onClick={pressAction}
					data-cy={'agent-row-action'}
				>
					{actionLabel}
				</ActionButton>
			</Tooltip>
		</FlexRow>
	);
};

type AgentRowActionsProps = {
	page: AgentPage;
	row: AgentRow;
};

export const AgentRowActions = ({page, row}: AgentRowActionsProps): JSX.Element | null => {
	const visibleAgents = getVisibleAgentsForRow(page, row.rowType);
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
			{visibleAgents.map(declaration => (
				<AgentRowAction key={declaration.id} declaration={declaration} row={row} />
			))}
		</FlexRow>
	);
};
